/**
 * Ad-click attribution for server-confirmed purchases.
 *
 * A purchase is confirmed on the server (Stripe webhook, Google Play / App
 * Store notifications) and reported to GA4 via Measurement Protocol. To credit
 * it to the right ad click the server needs what only the browser knows: the
 * GA client id and session id (the _ga cookies), the Google Ads click id
 * (gclid / gbraid / wbraid) and the utm tags. getAttributionPayload() builds
 * that; it goes as `attribution` in the body of every create-checkout POST
 * and in POST /api/analytics/attribution (stored on the profile for store
 * purchases).
 *
 * Click ids: last touch, kept 90 days in localStorage. A new click id
 * replaces the whole previous click; a URL without one keeps it. utm keeps
 * its own 30-day rule and key (shared/lib/utm.ts, 'utm_params').
 */
import { redactEmails, redactGiftTokens } from "@/features/activityLog/lib/sanitize";
import { captureUtmFromUrl, getStoredUtm } from "@/shared/lib/utm";
import { captureNoAnalyticsFlag } from "./internal";

export const GA4_MEASUREMENT_ID = "G-X6CFLRQ275";
const GA_SESSION_COOKIE = `_ga_${GA4_MEASUREMENT_ID.replace(/^G-/, "")}`;

export const CLICK_STORAGE_KEY = "analytics_click";
const LANDING_SESSION_KEY = "analytics_landing";
export const CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid"] as const;
const MAX_VALUE = 200;

type ClickKey = (typeof CLICK_KEYS)[number];

export interface StoredClick {
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    click_ts: number;
    landing_path?: string;
}

export interface AttributionPayload {
    ga_client_id?: string;
    ga_session_id?: string;
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    click_ts?: number;
    utm?: {
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        utm_term?: string;
    };
    landing_path?: string;
    consent?: "granted" | "denied" | "unknown";
}

const CLICK_ID_RE = /^[A-Za-z0-9_\-.~]{1,200}$/;

const cleanPath = (p: string): string => redactGiftTokens(redactEmails(p || "/")).slice(0, MAX_VALUE);

/** _ga = "GA1.1.123456789.1700000000" → "123456789.1700000000". */
export const parseGaClientId = (raw: string | null | undefined): string | undefined => {
    const parts = String(raw || "").split(".");
    if (parts.length < 4 || !/^GA\d/.test(parts[0])) return undefined;
    const a = parts[parts.length - 2];
    const b = parts[parts.length - 1];
    return /^\d+$/.test(a) && /^\d+$/.test(b) ? `${a}.${b}` : undefined;
};

/**
 * _ga_<STREAM> → session id. Old format "GS1.1.1700000000.5.1…" (third field),
 * new format "GS2.1.s1700000000$o5$g1$t…" (the `s` token).
 */
export const parseGaSessionId = (raw: string | null | undefined): string | undefined => {
    const parts = String(raw || "").split(".");
    if (parts.length < 3 || !/^GS\d/.test(parts[0])) return undefined;
    const third = parts[2];
    if (/^\d+$/.test(third)) return third;
    const tok = third.split("$").find((t) => /^s\d+$/.test(t));
    return tok ? tok.slice(1) : undefined;
};

export const readCookie = (name: string): string | null => {
    try {
        const prefix = `${name}=`;
        for (const c of document.cookie.split(";")) {
            const t = c.trim();
            if (t.startsWith(prefix)) return decodeURIComponent(t.slice(prefix.length));
        }
    } catch {
        // cookies blocked
    }
    return null;
};

export const getGaClientId = (): string | undefined => parseGaClientId(readCookie("_ga"));
export const getGaSessionId = (): string | undefined => parseGaSessionId(readCookie(GA_SESSION_COOKIE));

/** The click captured within the last 90 days, or null. */
export const getStoredClick = (now = Date.now()): StoredClick | null => {
    try {
        const raw = localStorage.getItem(CLICK_STORAGE_KEY);
        if (!raw) return null;
        const c = JSON.parse(raw) as StoredClick | null;
        if (!c || typeof c.click_ts !== "number" || !CLICK_KEYS.some((k) => typeof c[k] === "string")) {
            return null;
        }
        if (now - c.click_ts > CLICK_TTL_MS) {
            localStorage.removeItem(CLICK_STORAGE_KEY);
            return null;
        }
        return c;
    } catch {
        return null;
    }
};

/**
 * Read utm_* (via utm.ts), gclid / gbraid / wbraid and the landing path from
 * the current URL. Called on every location.search change (App.tsx).
 */
export const captureAttributionFromUrl = (): void => {
    captureUtmFromUrl();
    let search = "";
    let pathname = "/";
    try {
        search = window.location.search;
        pathname = window.location.pathname;
    } catch {
        return;
    }
    captureNoAnalyticsFlag(search);
    try {
        if (!sessionStorage.getItem(LANDING_SESSION_KEY)) {
            sessionStorage.setItem(LANDING_SESSION_KEY, cleanPath(pathname));
        }
    } catch {
        // per-tab landing is optional
    }
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(search);
    } catch {
        return;
    }
    const click: Partial<Record<ClickKey, string>> = {};
    for (const k of CLICK_KEYS) {
        const v = (params.get(k) || "").trim();
        if (v && CLICK_ID_RE.test(v)) click[k] = v;
    }
    if (Object.keys(click).length === 0) return;
    const prev = getStoredClick();
    // The same click id again (reload, Back) keeps the first capture time.
    if (prev && CLICK_KEYS.every((k) => prev[k] === click[k])) return;
    const stored: StoredClick = { ...click, click_ts: Date.now(), landing_path: cleanPath(pathname) };
    try {
        localStorage.setItem(CLICK_STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // storage blocked — attribution is best-effort
    }
};

const landingPath = (): string | undefined => {
    try {
        return sessionStorage.getItem(LANDING_SESSION_KEY) || undefined;
    } catch {
        return undefined;
    }
};

/** A short signature of the click ids — changes when a new ad click arrives. */
export const clickSignature = (): string => {
    const c = getStoredClick();
    return c ? CLICK_KEYS.map((k) => c[k] || "").join("|") : "";
};

/** The contract payload (see the header comment), or null outside a browser. */
export const getAttributionPayload = (): AttributionPayload | null => {
    if (typeof window === "undefined") return null;
    const p: AttributionPayload = {};
    const cid = getGaClientId();
    if (cid) p.ga_client_id = cid;
    const sid = getGaSessionId();
    if (sid) p.ga_session_id = sid;
    const click = getStoredClick();
    if (click) {
        for (const k of CLICK_KEYS) if (click[k]) p[k] = click[k]!.slice(0, MAX_VALUE);
        p.click_ts = click.click_ts;
    }
    const utm = getStoredUtm();
    if (utm) {
        const out: NonNullable<AttributionPayload["utm"]> = {};
        for (const [k, v] of Object.entries(utm)) {
            if (typeof v === "string" && v) {
                (out as Record<string, string>)[`utm_${k}`] = redactEmails(v).slice(0, MAX_VALUE);
            }
        }
        if (Object.keys(out).length > 0) p.utm = out;
    }
    const landing = click?.landing_path || landingPath();
    if (landing) p.landing_path = landing;
    // No consent banner yet (owner has not decided) — the server sees "unknown".
    p.consent = "unknown";
    return p;
};
